import React, { useEffect, useState } from 'react';
import { TextEditor } from './TextEditor';
import { JsonEditor } from './JsonEditor';
import { AudioPlayer } from './AudioPlayer';
import { VideoPlayer } from './VideoPlayer';
import { FileType, getFileType } from '../../utils/fileTypes';

interface MediaViewerProps {
  fileName: string;
  filePath: string;
  content?: string;
  onSave?: (content: string) => void;
  onClose?: () => void;
}

export const MediaViewer: React.FC<MediaViewerProps> = ({
  fileName,
  filePath,
  content,
  onSave,
  onClose
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fileContent, setFileContent] = useState<string>(content || '');

  // Keep internal state in sync with incoming prop updates
  useEffect(() => {
    setFileContent(content || '');
  }, [content]);
  
  const fileType = getFileType(fileName);
  
  const handleSave = async (updatedContent: string) => {
    if (onSave) {
      setIsLoading(true);
      try {
        await onSave(updatedContent);
        setFileContent(updatedContent);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to save file');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const renderViewer = () => {
    switch (fileType) {
      case FileType.TEXT:
        return (
          <TextEditor
            content={fileContent}
            fileName={fileName}
            onSave={handleSave}
            isLoading={isLoading}
            readonly={!onSave}
          />
        );
      
      case FileType.JSON:
        return (
          <JsonEditor
            content={fileContent}
            fileName={fileName}
            onSave={handleSave}
            isLoading={isLoading}
            readonly={!onSave}
          />
        );
      
      case FileType.AUDIO:
        return (
          <AudioPlayer
            src={filePath}
            fileName={fileName}
            onError={(errorMsg) => setError(errorMsg)}
          />
        );
      
      case FileType.VIDEO:
        return (
          <VideoPlayer
            src={filePath}
            fileName={fileName}
            onError={(errorMsg) => setError(errorMsg)}
            onClose={onClose}
          />
        );
      
      default:
        return (
          <div className="flex items-center justify-center h-64 text-gray-500">
            <div className="text-center">
              <div className="text-2xl mb-2">📄</div>
              <p>Unsupported file type: {fileName}</p>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="media-viewer">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          <p className="text-sm">{error}</p>
          <button 
            onClick={() => setError(null)}
            className="text-red-500 hover:text-red-700 text-xs underline mt-1"
          >
            Dismiss
          </button>
        </div>
      )}
      
      {renderViewer()}
    </div>
  );
};